import { Alert, Box, Stack } from '@ff-it/ui';
import { CreateScene, DeleteScene, EntityScene, IndexScene, ModelScene, UpdateScene, useEntity } from 'components';
import { bool } from 'components/columns';
import { BlockStatusTemplate } from 'modules/campaign/status/types';
import { RouteObject } from 'react-router';
import { Link } from 'react-router-dom';
import { Form } from './Form';
import { blockStates } from 'modules/campaign/block/types';
import { ReactElement } from 'react';
import { never } from 'core/permissions';
import { BlockStatusTag } from 'modules/campaign/status/BlockStatusTag';
import { ControlledTableColumns } from 'components/ControlledTable';

const model = {
  endpoint: 'campaign/statuses/',
  title: 'Statuses',
  pluralTitle: 'Statuses',
  entityTitle: ({ name }: BlockStatusTemplate) => name,
};

export const columns: ControlledTableColumns<BlockStatusTemplate> = [
  {
    content: 'Name',
    key: 'name',
    required: true,
    render: ({ id, name }: BlockStatusTemplate) => <Link to={id.toString()}>{name}</Link>,
    width: '250px',
  },
  {
    content: 'Statuses',
    key: 'statuses',
    render: ({ statuses }) => (
      <Stack direction="horizontal">
        {blockStates.map((state) =>
          statuses[state].map((status) => (
            <BlockStatusTag appearance="naked" key={status.id} status={{ ...status, state }} />
          )),
        )}
      </Stack>
    ),
  },
  bool('Default', 'is_system_default'),
];

const initialValues = {
  name: '',
  is_system_default: false,
  statuses: blockStates.reduce(
    (acc, state, _index) => {
      acc[state] = [
        {
          name: '',
          __NEW__: Date.now(),
        },
      ];
      return acc;
    },
    {} as Record<string, unknown>,
  ),
};

function UpdateView(): ReactElement {
  const {
    item: { is_system_default },
  } = useEntity<BlockStatusTemplate>();
  return (
    <UpdateScene disabled={is_system_default} canDelete={is_system_default ? never : undefined}>
      <Form>
        {is_system_default ? (
          <Alert variant="warning">Default system template is not editable</Alert>
        ) : (
          <Alert variant="warning" className="mb-2">
            <Box textSize="xs">
              When an existing status is removed, the Blocks (Media Plans) will automatically have their status replaced
              with the first status available in the associated state group.
            </Box>
          </Alert>
        )}
      </Form>
    </UpdateScene>
  );
}

export const route: RouteObject = {
  path: 'block_statuses',
  element: <ModelScene {...model} />,
  children: [
    {
      index: true,
      element: <IndexScene columns={columns} />,
    },
    {
      path: 'create',
      element: (
        <CreateScene initialValues={initialValues}>
          <Form />
        </CreateScene>
      ),
    },
    {
      path: ':id/',
      element: <EntityScene />,
      children: [
        {
          index: true,
          element: <UpdateView />,
        },
        {
          path: 'delete',
          element: <DeleteScene />,
        },
      ],
    },
  ],
};
